import { apiFetch } from '@/platform/lib/api';

export type AdminOverviewCounts = {
  users?: number;
  forumPosts?: number;
  groups?: number;
  claimsPublished?: number;
  claimsDraft?: number;
  vendorLinksPending?: number;
  vendorApplicationsPending?: number;
  vendorOrders?: number;
  materials?: number;
  materialLinksPending?: number;
};

export type AdminRollupLink = {
  id: string;
  label: string;
  href: string;
};

export type AdminRollupSection = {
  count: number;
  links?: AdminRollupLink[];
};

export type AdminRollupSnapshot = {
  date: string;
  computedAt: string;
  sections: Record<string, AdminRollupSection>;
  counts: AdminOverviewCounts;
};

export type AdminOverviewResponse = {
  counts: AdminOverviewCounts;
  sections?: Record<string, AdminRollupSection>;
  rollupDate?: string;
  computedAt?: string;
  approximate: boolean;
};

export function fetchAdminMe() {
  return apiFetch<{ isAdmin: boolean; isAgent?: boolean; portalAdmin?: boolean }>('/admin/me');
}

export function fetchAdminOverview() {
  return apiFetch<AdminOverviewResponse>('/admin/overview');
}

export function fetchAdminRollupCurrent() {
  return apiFetch<AdminRollupSnapshot>('/admin/rollup/current');
}

export function fetchAdminRollupDailyList() {
  return apiFetch<{ dates: string[] }>('/admin/rollup/daily');
}

export function fetchAdminRollupDaily(date: string) {
  return apiFetch<AdminRollupSnapshot>(`/admin/rollup/daily/${encodeURIComponent(date)}`);
}

export type AdminUserRow = {
  id: string;
  displayName: string;
  handle: string;
  email?: string;
  vendor: boolean;
  isAdmin: boolean;
  portalAdmin?: boolean;
  vendorVerificationStatus?: string;
  vendorStatus?: string;
  vendorSuspensionReason?: string;
  vendorDisciplinaryNotes?: string;
};

export function listAdminUsers(params?: { q?: string; cursor?: string }) {
  const q = new URLSearchParams();
  if (params?.q) q.set('q', params.q);
  if (params?.cursor) q.set('cursor', params.cursor);
  const qs = q.toString();
  return apiFetch<{ users: AdminUserRow[]; nextCursor?: string }>(`/admin/users${qs ? `?${qs}` : ''}`);
}

export function patchAdminUser(
  id: string,
  body: Partial<{
    isAdmin: boolean;
    portalAdmin: boolean;
    vendorStatus: string;
    vendorSuspensionReason: string;
    vendorDisciplinaryNotes: string;
  }>,
) {
  return apiFetch<void>(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
}

export type AdminForumPost = {
  id: string;
  title: string;
  authorId: string;
  subSlug?: string;
  createdAt: string;
  hiddenAt?: string;
};

export function listAdminForumPosts(params?: { subSlug?: string; cursor?: string; includeHidden?: boolean }) {
  const q = new URLSearchParams();
  if (params?.subSlug) q.set('subSlug', params.subSlug);
  if (params?.cursor) q.set('cursor', params.cursor);
  if (params?.includeHidden) q.set('includeHidden', 'true');
  const qs = q.toString();
  return apiFetch<{ posts: AdminForumPost[]; nextCursor?: string }>(
    `/admin/forum/posts${qs ? `?${qs}` : ''}`,
  );
}

export function patchAdminForumPost(id: string, hidden: boolean) {
  return apiFetch<void>(`/admin/forum/posts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ hidden }),
  });
}

export type AdminGroupRow = {
  id: string;
  groupId: string;
  name: string;
  visibility: string;
  memberCount: number;
};

export function listAdminGroups(params?: { cursor?: string }) {
  const q = new URLSearchParams();
  if (params?.cursor) q.set('cursor', params.cursor);
  const qs = q.toString();
  return apiFetch<{ groups: AdminGroupRow[]; nextCursor?: string }>(`/admin/groups${qs ? `?${qs}` : ''}`);
}

export type AdminGroupDetail = AdminGroupRow & {
  description?: string;
  ownerId: string;
  joinPolicy: string;
  postPolicy: string;
  status: string;
  calendarId?: string;
};

export function fetchAdminGroup(groupId: string) {
  return apiFetch<{ group: AdminGroupDetail; members: Array<{ userId: string; role: string; status: string }> }>(
    `/admin/groups/${encodeURIComponent(groupId)}`,
  );
}

export function patchAdminGroup(groupId: string, body: Record<string, unknown>) {
  return apiFetch<AdminGroupDetail>(`/admin/groups/${encodeURIComponent(groupId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function archiveAdminGroup(groupId: string) {
  return apiFetch<{ status: string }>(`/admin/groups/${encodeURIComponent(groupId)}`, { method: 'DELETE' });
}

export function patchAdminGroupMember(
  groupId: string,
  userId: string,
  body: { role?: string; status?: string },
) {
  return apiFetch<{ status: string }>(`/admin/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function removeAdminGroupMember(groupId: string, userId: string) {
  return apiFetch<{ status: string }>(
    `/admin/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}`,
    { method: 'DELETE' },
  );
}

export function transferAdminGroupOwner(groupId: string, newOwnerUserId: string) {
  return apiFetch<{ status: string }>(`/admin/groups/${encodeURIComponent(groupId)}/transfer-owner`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ newOwnerUserId }),
  });
}

export function listAdminGroupPosts(groupId: string) {
  return apiFetch<{ posts: Array<{ id: string; title: string; authorId: string; hiddenAt?: string }> }>(
    `/admin/groups/${encodeURIComponent(groupId)}/posts`,
  );
}

export function moderateAdminGroupPost(groupId: string, postId: string, action: string) {
  return apiFetch<{ status: string }>(
    `/admin/groups/${encodeURIComponent(groupId)}/posts/${encodeURIComponent(postId)}/moderate`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action }) },
  );
}

export type AdminGroupCalendar = {
  id: string;
  title: string;
  scope: string;
  groupId?: string;
  groupSlug?: string;
  editorUserIds: string[];
  timeZone: string;
};

export function fetchAdminGroupCalendar(groupId: string) {
  return apiFetch<AdminGroupCalendar>(`/admin/groups/${encodeURIComponent(groupId)}/calendar`);
}

export function createAdminGroupCalendar(groupId: string, body?: { title?: string; editorUserIds?: string[] }) {
  return apiFetch<AdminGroupCalendar>(`/admin/groups/${encodeURIComponent(groupId)}/calendar`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
}

export function patchAdminGroupCalendar(groupId: string, body: { title?: string; editorUserIds?: string[] }) {
  return apiFetch<AdminGroupCalendar>(`/admin/groups/${encodeURIComponent(groupId)}/calendar`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export type AdminClaimRow = {
  id: string;
  title: string;
  status: string;
  userId: string;
  roiPct?: number;
  createdAt: string;
};

export function listAdminClaims(params?: { status?: string; userId?: string; cursor?: string }) {
  const q = new URLSearchParams();
  if (params?.status) q.set('status', params.status);
  if (params?.userId) q.set('userId', params.userId);
  if (params?.cursor) q.set('cursor', params.cursor);
  const qs = q.toString();
  return apiFetch<{ claims: AdminClaimRow[]; nextCursor?: string }>(`/admin/claims${qs ? `?${qs}` : ''}`);
}

export function patchAdminClaim(id: string, hidden: boolean) {
  return apiFetch<void>(`/admin/claims/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ hidden }),
  });
}

export type AdminVendorLinkRow = {
  id: string;
  claimId: string;
  sellerUserId: string;
  status: string;
  purchasePageUrl: string;
  createdAt: string;
};

export function listAdminVendorLinks(params?: { status?: string; cursor?: string }) {
  const q = new URLSearchParams();
  if (params?.status) q.set('status', params.status);
  if (params?.cursor) q.set('cursor', params.cursor);
  const qs = q.toString();
  return apiFetch<{ requests: AdminVendorLinkRow[]; nextCursor?: string }>(
    `/admin/vendor-link-requests${qs ? `?${qs}` : ''}`,
  );
}

export type VendorApplicationRow = {
  id: string;
  userId: string;
  status: string;
  businessName: string;
  supportEmail: string;
  submittedAt?: string;
  connectChargesEnabled?: boolean;
  connectPayoutsEnabled?: boolean;
  connectOnboardingComplete?: boolean;
};

export function listVendorApplications(params?: { status?: string; cursor?: string }) {
  const q = new URLSearchParams();
  if (params?.status) q.set('status', params.status ?? 'submitted');
  if (params?.cursor) q.set('cursor', params.cursor ?? '');
  const qs = q.toString();
  return apiFetch<{ requests: VendorApplicationRow[]; nextCursor?: string }>(
    `/admin/vendor-verification-requests${qs ? `?${qs}` : ''}`,
  );
}

export function approveVendorApplication(id: string) {
  return apiFetch<{ status: string }>(`/admin/vendor-verification-requests/${id}/approve`, { method: 'POST' });
}

export function rejectVendorApplication(id: string, rejectionReason: string) {
  return apiFetch<{ status: string }>(`/admin/vendor-verification-requests/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ rejectionReason }),
  });
}

export type AdminVendorOrderRow = {
  id: string;
  sellerUserId: string;
  buyerUserId: string;
  status: string;
  totalCents: number;
  currency: string;
  createdAt: string;
};

export function listAdminVendorOrders(params?: { cursor?: string }) {
  const q = new URLSearchParams();
  if (params?.cursor) q.set('cursor', params.cursor);
  const qs = q.toString();
  return apiFetch<{ orders: AdminVendorOrderRow[]; nextCursor?: string }>(
    `/admin/vendor-orders${qs ? `?${qs}` : ''}`,
  );
}

export function formatOrderPrice(cents: number, currency = 'USD') {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
}
